import { ThemeEvents } from '@theme/events';
import { loadGsap } from './gsap-loader.js';
import { readWall, WALL_EVENT } from './wall-store.js';

/**
 * Full-screen menu. The panel is a modal <dialog>, so focus is trapped and Escape works natively; GSAP wipes it
 * open from the top and staggers the links in.
 */
class FullscreenMenuComponent extends HTMLElement {
  #abort = new AbortController();
  /** @type {any} */
  #gsap = null;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  #busy = false;
  #lastFocus = null;

  connectedCallback() {
    this.dialog = this.querySelector('[data-fsm-dialog]');
    this.trigger = this.querySelector('[data-fsm-open]');
    if (!this.dialog || !this.trigger) return;

    const { signal } = this.#abort;
    const on = (target, type, handler) => target?.addEventListener(type, handler, { signal });

    on(this.trigger, 'click', () => this.open());
    this.querySelectorAll('[data-fsm-close]').forEach((button) => on(button, 'click', () => this.close()));
    // Native Escape would close instantly; route it through the animated close instead.
    on(this.dialog, 'cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.querySelectorAll('a[href]').forEach((link) => on(link, 'click', (event) => this.#onLinkClick(event, link)));
    // Coming back with the browser's back button can restore the page with the menu still open.
    on(window, 'pageshow', (event) => {
      if (event.persisted && (this.dialog.open || this.dialog.classList.contains('is-leaving'))) this.close(true);
    });
    on(document, ThemeEvents.cartUpdate, () => this.#refreshCartCount());
    on(window, WALL_EVENT, () => this.#refreshWallCount());
    on(window, 'storage', (event) => {
      if (event.key === 'melonary:wall') this.#refreshWallCount();
    });
    this.#refreshWallCount();

    loadGsap().then((gsap) => {
      this.#gsap = gsap;
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#unlockScroll();
    this.#gsap?.killTweensOf(this.dialog);
  }

  async open() {
    if (this.dialog.open || this.#busy) return;
    this.#busy = true;
    this.#lastFocus = document.activeElement;

    this.dialog.showModal();
    // Keep focus on the panel, not the first link, so the logo isn't highlighted on open.
    this.dialog.focus({ preventScroll: true });
    this.trigger.setAttribute('aria-expanded', 'true');
    this.#refreshWallCount();
    this.#lockScroll();

    const gsap = this.#gsap ?? (await loadGsap());
    this.#gsap = gsap;
    if (!gsap || this.#reduceMotion) {
      this.#busy = false;
      return;
    }

    const items = this.dialog.querySelectorAll('[data-fsm-reveal]');
    gsap
      .timeline({ onComplete: () => (this.#busy = false) })
      .fromTo(
        this.dialog,
        { clipPath: 'inset(0 0 100% 0)' },
        { clipPath: 'inset(0 0 0% 0)', duration: 0.75, ease: 'expo.inOut', clearProps: 'clipPath' }
      )
      .from(
        items,
        { y: 46, opacity: 0, duration: 0.8, ease: 'expo.out', stagger: 0.05, clearProps: 'opacity,transform' },
        0.3
      );
  }

  /** @param {boolean} [immediate] close without the wipe, e.g. when following a link. */
  async close(immediate = false) {
    if (!this.dialog.open) return;
    const gsap = this.#gsap;

    const finish = () => {
      this.dialog.close();
      gsap?.set(this.dialog, { clearProps: 'clipPath' });
      this.trigger.setAttribute('aria-expanded', 'false');
      this.#unlockScroll();
      this.#busy = false;
      if (!immediate) {
        // Some browsers don't focus a button on click, so fall back to the Menu button itself.
        const target = this.#lastFocus?.isConnected && this.#lastFocus !== document.body ? this.#lastFocus : this.trigger;
        target.focus({ preventScroll: true });
      }
    };

    if (!gsap || this.#reduceMotion || immediate) {
      finish();
      return;
    }

    this.#busy = true;
    gsap.killTweensOf([this.dialog, ...this.dialog.querySelectorAll('[data-fsm-reveal]')]);
    gsap.fromTo(
      this.dialog,
      { clipPath: 'inset(0 0 0% 0)' },
      { clipPath: 'inset(0 0 100% 0)', duration: 0.6, ease: 'expo.inOut', onComplete: finish }
    );
  }

  /**
   * A link to another page keeps the menu open while the browser loads it, so the old page never shows through
   * between the menu closing and the new page arriving. Only links that stay on this page close the menu.
   */
  #onLinkClick(event, link) {
    const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
    if (event.defaultPrevented || modified || link.target === '_blank') return;

    const url = new URL(link.href, window.location.href);
    const samePage =
      url.origin === window.location.origin && url.pathname === window.location.pathname && url.search === window.location.search;
    if (samePage) {
      this.close(true);
      return;
    }

    this.dialog.classList.add('is-leaving');
    link.classList.add('is-active');
    // A hard cut from the menu to the new page, not the theme's slide and fade of <main>.
    window.addEventListener(
      'pageswap',
      (swap) => {
        const transition = swap.viewTransition;
        if (!transition) return;
        transition.skipTransition();
        [transition.ready, transition.finished, transition.updateCallbackDone].forEach((promise) => promise?.catch(() => {}));
      },
      { once: true }
    );
    // If the navigation never happens (offline, blocked), give the page back.
    setTimeout(() => {
      this.dialog.classList.remove('is-leaving');
      link.classList.remove('is-active');
    }, 8000);
  }

  #lockScroll() {
    window.lenis?.stop?.();
    document.documentElement.style.overflow = 'hidden';
  }

  #unlockScroll() {
    window.lenis?.start?.();
    document.documentElement.style.overflow = '';
  }

  /** The wall lives in this browser, so the count is read here rather than rendered by the server. */
  #refreshWallCount() {
    const target = this.querySelector('[data-fsm-wall-count]');
    if (!target) return;
    const count = readWall().items.length;
    target.textContent = count ? `${count} on wall` : 'New';
  }

  async #refreshCartCount() {
    const target = this.querySelector('[data-fsm-cart-count]');
    if (!target) return;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      target.textContent = `(${cart.item_count})`;
    } catch {
      // The count is a nicety; leave the old value if the request fails.
    }
  }
}

if (!customElements.get('fullscreen-menu-component')) {
  customElements.define('fullscreen-menu-component', FullscreenMenuComponent);
}
